import React, { useState, useEffect } from 'react';
import axios from 'cccisd-axios';
import { client } from 'cccisd-apollo';
import _get from 'lodash/get';
import { DeploymentPlayer } from 'cccisd-laravel-assignment';
import IconTarget from 'cccisd-icons/target';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import Tabs from 'cccisd-tabs';
import Table from 'cccisd-table';
import { getGoalsParentDeploymentId } from '../../../helpers/helpers.js';
import goalsQuery from './goals.graphql';
import style from './style.css';

export const GOALS_DEPLOYMENT_HANDLE = 'tfh_instructor_goals';

const Boilerplate = window.cccisd.boilerplate;

const Goals = ({ member, memberDisplayName }) => {
    // need state so only 1 DeploymentPlayer is mounted on the page at a time
    const [isOpen, setIsOpen] = useState(false);
    const [goalsParentDeploymentId, setGoalsParentDeploymentId] = useState(null);
    const [goalsData, setGoalsData] = useState(null);

    const pawnId = _get(member, 'pawn.pawnId');
    const pawnHash = _get(member, 'pawn.pawnHash');

    /* Used to clear flow state if Deployment was not officially submitted, keeping the 2 in sync
        from the Instrutor's perspective */
    async function eraseIncomplete() {
        if (!goalsParentDeploymentId) {
            return;
        }

        await axios.post(Boilerplate.route('api.app.dailyTracking.eraseMetricsIfIncomplete'), {
            pawnId,
            pawnHash,
            deploymentId: goalsParentDeploymentId,
        });
    }

    async function getData() {
        if (!goalsParentDeploymentId) {
            return;
        }

        const resp = await client.query({
            query: goalsQuery,
            variables: { pawnId, pawnHash, goalsParentDeploymentId },
            fetchPolicy: 'network-only',
        });

        setGoalsData(_get(resp, 'data.roles.anyRole.assignmentProgressList', []) || []);
    }

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (!goalsParentDeploymentId) {
            getGoalsParentDeploymentId().then(id => {
                setGoalsParentDeploymentId(id);
            });
        } else {
            eraseIncomplete();
            getData();
        }
    }, [isOpen, goalsParentDeploymentId]);

    const trigger = (
        <button className="btn btn-sm btn-secondary" type="button">
            <IconTarget spaceRight />
            Goals
        </button>
    );

    return (
        <Modal
            title={`Goals - ${memberDisplayName}`}
            trigger={trigger}
            beforeShow={() => setIsOpen(true)}
            afterClose={() => setIsOpen(false)}
            size="large"
            render={({ closeModal }) => {
                if (!isOpen || !goalsParentDeploymentId || !goalsData) {
                    return <Loader type="inline" loading />;
                }

                const flowVars = {
                    goalsCalorie: 'n/a',
                    goalsRedFood: 'n/a',
                    goalsGreenFood: 'n/a',
                    goalsActivity: 'n/a',
                };

                goalsData
                    // oldest first, because if field is not filled out, then it defaults to previous submission
                    .sort((a, b) => {
                        return a.deploymentId - b.deploymentId;
                    })
                    .forEach(ap => {
                        ['goalsCalorie', 'goalsRedFood', 'goalsGreenFood', 'goalsActivity'].forEach(k => {
                            if (k in ap.devTags && ap.devTags[k]) {
                                flowVars[k] = ap.devTags[k];
                            }
                        });
                    });

                const tabList = [
                    {
                        name: 'history',
                        title: 'History',
                        content: (
                            <div>
                                <div className="alert alert-primary mb-5">
                                    <h3 className="text-primary">Current Goals:</h3>
                                    <table className={style.goalsSummary}>
                                        <tr>
                                            <td>Calories:</td>
                                            <td>
                                                {Number.isNaN(parseInt(flowVars.goalsCalorie, 10))
                                                    ? '-'
                                                    : `${flowVars.goalsCalorie}-${
                                                          parseInt(flowVars.goalsCalorie, 10) + 200
                                                      } kcal/day`}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>RED Food:</td>
                                            <td>
                                                {Number.isNaN(parseInt(flowVars.goalsRedFood, 10))
                                                    ? '-'
                                                    : `${flowVars.goalsRedFood} servings/day`}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>GREEN Food:</td>
                                            <td>
                                                {Number.isNaN(parseInt(flowVars.goalsGreenFood, 10))
                                                    ? '-'
                                                    : `${flowVars.goalsGreenFood} servings/day`}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Activity:</td>
                                            <td>
                                                {Number.isNaN(parseInt(flowVars.goalsActivity, 10))
                                                    ? '-'
                                                    : `${flowVars.goalsActivity} minutes/day`}
                                            </td>
                                        </tr>
                                    </table>
                                </div>
                                <h3 className="text-primary">History:</h3>
                                <Table
                                    rowKey="deploymentId"
                                    name="previousGoals"
                                    showRowActions={false}
                                    isAscentOrder={false}
                                    showPerPageOptions={false}
                                    perPage={100}
                                    hideShowingRowsSummary
                                    noRecordsMessage="No goals have been set yet."
                                    columns={[
                                        {
                                            name: 'lastVisitedDate',
                                            label: 'Date',
                                            sort: true,
                                            filter: true,
                                            filterSettings: { type: 'date' },
                                        },
                                        {
                                            name: 'goalsCalorie',
                                            label: 'Calorie Minimum',
                                            tooltip: 'This will display as a 200-calorie range for each user.',
                                        },
                                        { name: 'goalsRedFood', label: 'RED Food Goal' },
                                        { name: 'goalsGreenFood', label: 'GREEN Food Goal' },
                                        { name: 'goalsActivity', label: 'Activity Goal' },
                                    ]}
                                    data={goalsData.reduce((all, ap) => {
                                        const data = {
                                            deploymentId: ap.deploymentId,
                                            lastVisitedDate: ap.lastVisitedDate,
                                            goalsCalorie: ap.devTags.goalsCalorie,
                                            goalsRedFood: ap.devTags.goalsRedFood,
                                            goalsGreenFood: ap.devTags.goalsGreenFood,
                                            goalsActivity: ap.devTags.goalsActivity,
                                        };

                                        /* If goals are totally blank, don't include it in historical record */
                                        if (
                                            data.goalsCalorie ||
                                            data.goalsRedFood ||
                                            data.goalsGreenFood ||
                                            data.goalsActivity
                                        ) {
                                            all.push(data);
                                        }

                                        return all;
                                    }, [])}
                                />
                                <div className="mt-4 text-end">
                                    <button className="btn btn-secondary" type="button" onClick={() => closeModal()}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        ),
                    },
                    {
                        name: 'update',
                        title: 'Update Goals',
                        content: (
                            <DeploymentPlayer
                                disableLayout
                                deploymentHandle={GOALS_DEPLOYMENT_HANDLE}
                                pawnId={pawnId}
                                pawnHash={pawnHash}
                                onComplete={() => {
                                    // purposefully don't await so metrics can save before unmounting
                                    setTimeout(() => {
                                        closeModal();
                                    }, 1000);
                                }}
                                flowProps={{
                                    variables: {
                                        goalsPrevCalorieGoal: flowVars.goalsCalorie,
                                        goalsPrevRedGoal: flowVars.goalsRedFood,
                                        goalsPrevGreenGoal: flowVars.goalsGreenFood,
                                        goalsPrevActivityGoal: flowVars.goalsActivity,
                                    },
                                }}
                            />
                        ),
                    },
                ];

                return (
                    <Tabs
                        tabList={tabList}
                        initialTab="history"
                        onTabSelect={newTab => {
                            /* If switching from Update to History,
                                clear response state so Update appears
                                blank when Instructor revisits the unsaved survey */
                            if (newTab === 'history') {
                                eraseIncomplete();
                            }
                        }}
                    />
                );
            }}
        />
    );
};

export default Goals;
